import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  MapPin, 
  Check, 
  X, 
  ExternalLink, 
  AlertCircle,
  Loader2,
  Users,
  Building,
  Home,
  HelpCircle
} from 'lucide-react';

// Types
interface CohortMember {
  id: string;
  cohortId: string;
  sellerId: string;
  originalName: string;
  workingName: string;
  originalAddress: string;
  workingAddress: string;
  problemFlags: {
    isDuplicate?: 'NEEDS_REVIEW' | 'APPROVED' | 'DELETED';
    isLongName?: 'NEEDS_REVIEW' | 'APPROVED' | 'DELETED';
    isAllCaps?: 'NEEDS_REVIEW' | 'APPROVED' | 'DELETED';
    isAllSmall?: 'NEEDS_REVIEW' | 'APPROVED' | 'DELETED';
  };
  addressHelperData?: {
    addressType?: 'BUSINESS' | 'RESIDENTIAL' | 'UNKNOWN';
    latitude?: number;
    longitude?: number;
  };
}

interface DuplicateGroup {
  address: string;
  members: CohortMember[];
  addressType?: 'BUSINESS' | 'RESIDENTIAL' | 'UNKNOWN';
  latitude?: number;
  longitude?: number;
}

interface DuplicateResolverProps {
  members: CohortMember[];
  onResolveProblem: (
    cohortMemberId: string, 
    problemToFix: string, 
    action: 'APPROVE' | 'DELETE'
  ) => Promise<void>;
}

const DuplicateResolver: React.FC<DuplicateResolverProps> = ({
  members,
  onResolveProblem
}) => {
  const [processingStates, setProcessingStates] = useState<{ [key: string]: boolean }>({});

  // Group members by their working address
  const duplicateGroups = useMemo(() => {
    const groups: { [address: string]: DuplicateGroup } = {};
    
    members.forEach(member => {
      const address = member.workingAddress;
      if (!groups[address]) {
        groups[address] = {
          address,
          members: [],
          addressType: member.addressHelperData?.addressType,
          latitude: member.addressHelperData?.latitude,
          longitude: member.addressHelperData?.longitude,
        };
      }
      groups[address].members.push(member);
    });

    // Only return groups with more than one member (actual duplicates)
    return Object.values(groups).filter(group => group.members.length > 1);
  }, [members]);

  // Get address type icon and color
  const getAddressTypeInfo = (type?: string) => {
    switch (type) {
      case 'BUSINESS':
        return { 
          icon: Building, 
          color: 'text-blue-600 dark:text-blue-400',
          bgColor: 'bg-blue-100 dark:bg-blue-900/20',
          label: 'Business'
        };
      case 'RESIDENTIAL':
        return { 
          icon: Home, 
          color: 'text-green-600 dark:text-green-400',
          bgColor: 'bg-green-100 dark:bg-green-900/20',
          label: 'Residential'
        };
      case 'UNKNOWN':
      default:
        return { 
          icon: HelpCircle, 
          color: 'text-gray-600 dark:text-gray-400',
          bgColor: 'bg-gray-100 dark:bg-gray-800',
          label: 'Unknown'
        };
    }
  };

  // Generate Google Maps link
  const getGoogleMapsLink = (latitude?: number, longitude?: number, address?: string) => {
    if (latitude && longitude) {
      return `https://www.google.com/maps?q=${latitude},${longitude}`;
    } else if (address) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    }
    return null;
  };

  // Handle approve action
  const handleApprove = async (member: CohortMember) => {
    setProcessingStates(prev => ({ ...prev, [member.id]: true }));
    
    try {
      await onResolveProblem(member.id, 'isDuplicate', 'APPROVE');
    } catch (error) {
      console.error('Error approving duplicate:', error);
      // Error handling could be improved with toast notifications
    } finally {
      setProcessingStates(prev => ({ ...prev, [member.id]: false }));
    }
  };

  // Handle delete action
  const handleDelete = async (member: CohortMember) => {
    setProcessingStates(prev => ({ ...prev, [member.id]: true }));
    
    try {
      await onResolveProblem(member.id, 'isDuplicate', 'DELETE');
    } catch (error) {
      console.error('Error deleting duplicate:', error);
      // Error handling could be improved with toast notifications
    } finally {
      setProcessingStates(prev => ({ ...prev, [member.id]: false }));
    }
  };

  if (duplicateGroups.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <MapPin className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No Duplicate Address Issues
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            All duplicate addresses in this cohort have been resolved.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
            Duplicate Address Resolution
          </CardTitle>
          <p className="text-gray-600 dark:text-gray-400">
            Multiple sellers share the same address. Review each group and decide which sellers to keep or remove.
            Use the address helper data and Google Maps link to verify the location.
          </p>
        </CardHeader>
      </Card>

      {/* Duplicate Groups */}
      {duplicateGroups.map((group, groupIndex) => {
        const addressTypeInfo = getAddressTypeInfo(group.addressType);
        const AddressIcon = addressTypeInfo.icon;
        const mapsLink = getGoogleMapsLink(group.latitude, group.longitude, group.address);

        return (
          <Card key={`${group.address}-${groupIndex}`} className="border-l-4 border-l-red-500">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg font-semibold mb-2">
                    Duplicate Address Group
                  </CardTitle>
                  
                  {/* Address Info */}
                  <div className="flex items-start space-x-3 mb-3">
                    <div className={`p-2 ${addressTypeInfo.bgColor} rounded-lg`}>
                      <MapPin className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {group.address}
                      </p>
                      <div className="flex items-center space-x-4 mt-1">
                        <div className="flex items-center">
                          <AddressIcon className={`h-3 w-3 ${addressTypeInfo.color} mr-1`} />
                          <Badge variant="secondary" className="text-xs">
                            {addressTypeInfo.label}
                          </Badge>
                        </div>
                        {mapsLink && (
                          <a
                            href={mapsLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            View on Maps
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Coordinates (if available) */}
                  {group.latitude && group.longitude && (
                    <div className="text-xs text-gray-500 dark:text-gray-500">
                      Coordinates: {group.latitude.toFixed(6)}, {group.longitude.toFixed(6)}
                    </div>
                  )}
                </div>

                <Badge variant="secondary" className="bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400">
                  <Users className="h-3 w-3 mr-1" />
                  {group.members.length} Sellers
                </Badge>
              </div>
            </CardHeader>

            <CardContent>
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                  Choose which sellers to keep or remove:
                </h4>

                {group.members.map((member) => {
                  const isProcessing = processingStates[member.id];

                  return (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-white dark:bg-gray-700 rounded-lg">
                            <Users className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {member.workingName}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-500">
                              Seller ID: {member.sellerId}
                            </p>
                            {member.originalName !== member.workingName && (
                              <p className="text-xs text-gray-400 dark:text-gray-600">
                                Original: {member.originalName}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex space-x-2 ml-4">
                        <Button
                          onClick={() => handleApprove(member)}
                          disabled={isProcessing}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          {isProcessing ? (
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          ) : (
                            <Check className="h-3 w-3 mr-1" />
                          )}
                          Approve
                        </Button>
                        
                        <Button
                          onClick={() => handleDelete(member)}
                          disabled={isProcessing}
                          variant="destructive"
                          size="sm"
                        >
                          {isProcessing ? (
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          ) : (
                            <X className="h-3 w-3 mr-1" />
                          )}
                          Delete
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Helper Text */}
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/10 rounded-lg">
                <div className="flex items-start">
                  <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2 mt-0.5" />
                  <div className="text-xs text-blue-800 dark:text-blue-300">
                    <p className="font-medium mb-1">Resolution Tips:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>For businesses: Keep all sellers (they may be different departments)</li>
                      <li>For residences: Usually keep only one seller per address</li>
                      <li>Check Google Maps to verify if it's a business or residential location</li>
                      <li>When in doubt, approve all and let the next phase handle verification</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default DuplicateResolver;